import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import courseRequestService from "./courseRequestService";  // Ensure you have this service file to handle the API calls.
import { toast } from 'react-toastify';

// Thunks for course request operations
export const getCourseRequests = createAsyncThunk(
  "courseRequest/get-course-requests",
  async (thunkAPI) => {
    try {
      return await courseRequestService.getCourseRequests();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteACourseRequest = createAsyncThunk(
  "courseRequest/delete-course-request",
  async (id, thunkAPI) => {
    try {
      return await courseRequestService.deleteCourseRequest(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getACourseRequest = createAsyncThunk(
  "courseRequest/get-course-request",
  async (id, thunkAPI) => {
    try {
      return await courseRequestService.getCourseRequest(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateACourseRequest = createAsyncThunk(
  "courseRequest/update-course-request",
  async (courseRequest, thunkAPI) => {
    try {
      return await courseRequestService.updateCourseRequest(courseRequest);
    } catch (error) {
      // Extract serializable parts of the Axios error
      const serializableError = {
        message: error.message,
        code: error.code,
        response: error.response ? error.response.data : null,
      };
      return thunkAPI.rejectWithValue(serializableError);
    }
  }
);

export const getUnreadCourseRequests = createAsyncThunk(
  "courseRequest/get-unread-course-requests",
  async (_, thunkAPI) => {
    try {
      return await courseRequestService.getUnreadCourseRequests();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const markCourseRequestAsRead = createAsyncThunk(
  "courseRequest/mark-as-read",
  async (id, thunkAPI) => {
    try {
      return await courseRequestService.markCourseRequestAsRead(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Reset state action
export const resetState = createAction("Reset_all");

const initialState = {
  courseRequests: [],
  unreadCourseRequests: [],
  isError: false,
  isLoading: false,
  isSuccess: false,
  message: "",
};

export const courseRequestSlice = createSlice({
  name: "courseRequests",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch course requests
      .addCase(getCourseRequests.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCourseRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.courseRequests = action.payload;
      })
      .addCase(getCourseRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      // Delete course request
      .addCase(deleteACourseRequest.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteACourseRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.deletedCourseRequest = action.payload;
      })
      .addCase(deleteACourseRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      // Get a single course request
      .addCase(getACourseRequest.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getACourseRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.courseRequestDetails = action.payload;  // Store the details of the specific course request
      })
      .addCase(getACourseRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      // Update a course request
      .addCase(updateACourseRequest.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateACourseRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.updatedCourseRequest = action.payload;
      })
      .addCase(updateACourseRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.error;
      })
      // Get unread course requests
      .addCase(getUnreadCourseRequests.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUnreadCourseRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.unreadCourseRequests = action.payload.courseRequests;
      })
      .addCase(getUnreadCourseRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.error.message || "Failed to fetch unread course requests";
      })
      // Mark course request as read
      .addCase(markCourseRequestAsRead.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(markCourseRequestAsRead.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = "Course request marked as read successfully";
      })
      .addCase(markCourseRequestAsRead.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.error.message || "Failed to mark course request as read";
      })
      // Reset the state
      .addCase(resetState, () => initialState);
  },
});

export default courseRequestSlice.reducer;
